import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { METRIC_COLORS, TOKENS } from "../lib/designTokens";
import * as SliderPrimitive from "@radix-ui/react-slider";
import { ToggleGroup, ToggleGroupItem } from "./ui/toggle-group";
import { StatBlock } from "./StatBlock";
import { distanceUnit, distanceValue, fmtDuration, fmtSpeed } from "../lib/format";
import { fmtIF } from "../lib/powerMetrics";
import {
  formatRangeSummary,
  formatDurationHms,
  parseTimeInput,
  parseDistanceInput,
} from "../lib/rangeSelection";

export const SegmentStatsPanel = ({
  stats,
  loading = false,
  sport,
  rangeSummary,
  onClear,
  unitSystem = "metric",
}) => {
  if (!stats && !loading) return null;

  return (
    <div
      className="app-card-elevated border border-brand-primary/30 bg-brand-primary/5 p-5 space-y-4"
      data-testid="segment-stats-panel"
    >
      {loading && !stats && (
        <p className="text-xs text-dark-text-secondary" data-testid="segment-stats-loading">
          Calculating segment metrics…
        </p>
      )}
      {!stats ? null : (
      <>
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="card-label text-brand-primary">Selected Range</div>
          {rangeSummary && (
            <>
              <div className="section-title mt-1 text-base">
                {rangeSummary.primary}
              </div>
              <p className="meta-text mt-1">{rangeSummary.secondary}</p>
            </>
          )}
        </div>
        <button
          type="button"
          onClick={onClear}
          className="btn-secondary-app px-3 py-2 text-[10px] tracking-[0.2em] uppercase"
          data-testid="clear-range-selection"
        >
          Reset Full Activity
        </button>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <StatBlock
          label="Distance"
          value={
            stats.distance_m != null
              ? distanceValue(stats.distance_m, unitSystem).toFixed(2)
              : null
          }
          unit={distanceUnit(unitSystem)}
          accent={TOKENS.analytics.blue}
          testId="segment-distance"
        />
        <StatBlock
          label="Duration"
          value={stats.duration_seconds != null ? fmtDuration(stats.duration_seconds) : null}
          testId="segment-duration"
        />
        <StatBlock
          label="Avg Speed"
          value={fmtSpeed(stats.avg_speed_ms, sport, unitSystem)}
          testId="segment-avg-speed"
        />
        <StatBlock
          label="Max Speed"
          value={fmtSpeed(stats.max_speed_ms, sport, unitSystem)}
          testId="segment-max-speed"
        />
        {stats.has_elevation && (
          <>
            <StatBlock
              label="Elev Gain"
              value={stats.elevation_gain_m?.toFixed(0)}
              unit="m"
              accent={TOKENS.analytics.green}
              testId="segment-elevation-gain"
            />
            <StatBlock
              label="Elev Loss"
              value={stats.elevation_loss_m?.toFixed(0)}
              unit="m"
              accent={TOKENS.analytics.green}
              testId="segment-elevation-loss"
            />
          </>
        )}
        {stats.has_hr && (
          <>
            <StatBlock
              label="Avg HR"
              value={stats.avg_hr != null ? Math.round(stats.avg_hr) : null}
              unit="bpm"
              accent={METRIC_COLORS.hr}
              testId="segment-avg-hr"
            />
            <StatBlock
              label="Max HR"
              value={stats.max_hr != null ? Math.round(stats.max_hr) : null}
              unit="bpm"
              accent={METRIC_COLORS.hr}
              testId="segment-max-hr"
            />
          </>
        )}
        {stats.has_power && (
          <>
            <StatBlock
              label="Avg Power"
              value={stats.avg_power != null ? Math.round(stats.avg_power) : null}
              unit="W"
              accent={METRIC_COLORS.power}
              testId="segment-avg-power"
            />
            <StatBlock
              label="Max Power"
              value={stats.max_power != null ? Math.round(stats.max_power) : null}
              unit="W"
              accent={METRIC_COLORS.power}
              testId="segment-max-power"
            />
          </>
        )}
        {stats.has_cadence && (
          <>
            <StatBlock
              label="Avg Cadence"
              value={stats.avg_cadence != null ? Math.round(stats.avg_cadence) : null}
              unit="rpm"
              accent={METRIC_COLORS.cadence}
              testId="segment-avg-cadence"
            />
            <StatBlock
              label="Max Cadence"
              value={stats.max_cadence != null ? Math.round(stats.max_cadence) : null}
              unit="rpm"
              accent={METRIC_COLORS.cadence}
              testId="segment-max-cadence"
            />
          </>
        )}
        {stats.normalized_power != null && (
          <>
            <StatBlock
              label="Norm. Power"
              value={stats.normalized_power}
              unit="W"
              accent={METRIC_COLORS.power}
              testId="segment-np"
            />
            <StatBlock
              label="Intensity"
              value={fmtIF(stats.intensity_factor)}
              testId="segment-if"
            />
            <StatBlock
              label="TSS"
              value={stats.tss}
              accent={METRIC_COLORS.tss}
              testId="segment-tss"
            />
          </>
        )}
        {stats.calories != null && (
          <StatBlock
            label="Calories"
            value={Math.round(stats.calories)}
            unit="kcal"
            accent={METRIC_COLORS.temperature}
            testId="segment-calories"
          />
        )}
      </div>
      </>
      )}
    </div>
  );
};

export const ActivityRangeSelector = ({
  samples,
  activityStartTime,
  mode,
  onModeChange,
  startIndex,
  endIndex,
  onRangeChange,
  bounds,
  unitSystem = "metric",
}) => {
  const unit = distanceUnit(unitSystem);
  const domainMax =
    mode === "distance" && bounds.hasDistance
      ? distanceValue(bounds.totalDistanceM ?? 0, unitSystem) ?? 0
      : bounds.totalDurationS;

  const [domainValues, setDomainValues] = useState([0, domainMax]);
  const [startInput, setStartInput] = useState("");
  const [endInput, setEndInput] = useState("");
  const panRef = useRef(null);
  const panStateRef = useRef(null);
  const latestDomainRef = useRef([0, domainMax]);

  useEffect(() => {
    latestDomainRef.current = domainValues;
  }, [domainValues]);

  useEffect(() => {
    const handleMove = (e) => {
      if (!panStateRef.current) return;
      const width = panRef.current?.getBoundingClientRect().width ?? 1;
      const deltaDomain = ((e.clientX - panStateRef.current.startX) / width) * domainMax;
      const span = panStateRef.current.span;
      let nextStart = panStateRef.current.startVal + deltaDomain;
      nextStart = Math.max(0, Math.min(domainMax - span, nextStart));
      const next = [nextStart, nextStart + span];
      latestDomainRef.current = next;
      setDomainValues(next);
      onRangeChange(next[0], next[1], { commit: false });
    };

    const handleUp = () => {
      if (!panStateRef.current) return;
      panStateRef.current = null;
      const [a, b] = latestDomainRef.current;
      onRangeChange(a, b, { commit: true });
    };

    window.addEventListener("pointermove", handleMove);
    window.addEventListener("pointerup", handleUp);
    return () => {
      window.removeEventListener("pointermove", handleMove);
      window.removeEventListener("pointerup", handleUp);
    };
  }, [domainMax, onRangeChange]);

  useEffect(() => {
    const [startVal, endVal] =
      mode === "distance" && bounds.hasDistance
        ? (() => {
            const baseM = samples[0]?.distance_m ?? 0;
            const d0 = (samples[startIndex]?.distance_m ?? baseM) - baseM;
            const d1 = (samples[endIndex]?.distance_m ?? baseM) - baseM;
            return [
              distanceValue(d0, unitSystem) ?? 0,
              distanceValue(d1, unitSystem) ?? 0,
            ];
          })()
        : (() => {
            const originMs = bounds.originMs;
            const startSec = samples[startIndex]?.t && originMs
              ? Math.max(0, (new Date(samples[startIndex].t).getTime() - originMs) / 1000)
              : startIndex;
            const endSec = samples[endIndex]?.t && originMs
              ? Math.max(0, (new Date(samples[endIndex].t).getTime() - originMs) / 1000)
              : endIndex;
            return [startSec, endSec];
          })();
    setDomainValues([startVal, endVal]);
    setStartInput(
      mode === "distance" && bounds.hasDistance
        ? startVal.toFixed(2)
        : formatDurationHms(startVal)
    );
    setEndInput(
      mode === "distance" && bounds.hasDistance
        ? endVal.toFixed(2)
        : formatDurationHms(endVal)
    );
  }, [mode, startIndex, endIndex, samples, bounds, unitSystem]);

  const step =
    mode === "distance" && bounds.hasDistance
      ? Math.max(0.001, (bounds.totalDistanceKm ?? 1) / 2000)
      : Math.max(1, (bounds.totalDurationS ?? 1) / 2000);

  const handleSliderChange = useCallback(
    (values) => {
      const [a, b] = values;
      setDomainValues([a, b]);
      onRangeChange(a, b, { commit: false });
    },
    [onRangeChange]
  );

  const handleSliderCommit = useCallback(
    (values) => {
      const [a, b] = values;
      onRangeChange(a, b, { commit: true });
    },
    [onRangeChange]
  );

  const applyManualInputs = useCallback(() => {
    const parseStart =
      mode === "distance" && bounds.hasDistance
        ? parseDistanceInput(startInput, unitSystem)
        : parseTimeInput(startInput);
    const parseEnd =
      mode === "distance" && bounds.hasDistance
        ? parseDistanceInput(endInput, unitSystem)
        : parseTimeInput(endInput);
    if (!Number.isFinite(parseStart) || !Number.isFinite(parseEnd)) return;
    const lo = Math.max(0, Math.min(parseStart, parseEnd));
    const hi = Math.min(domainMax, Math.max(parseStart, parseEnd));
    setDomainValues([lo, hi]);
    onRangeChange(lo, hi, { commit: true });
  }, [mode, bounds.hasDistance, startInput, endInput, domainMax, onRangeChange, unitSystem]);

  const rangeSummary = formatRangeSummary(
    mode,
    samples,
    startIndex,
    endIndex,
    bounds,
    unitSystem
  );

  if (!samples.length || domainMax <= 0) return null;

  return (
    <div className="app-card p-4 sm:p-5 space-y-4" data-testid="activity-range-selector">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <div className="card-label">Range Selection</div>
          <div className="text-xs font-mono text-dark-text-secondary mt-1">{rangeSummary.primary}</div>
          <div className="meta-text font-mono mt-0.5">{rangeSummary.secondary}</div>
        </div>

        <ToggleGroup
          type="single"
          value={mode}
          onValueChange={(v) => v && onModeChange(v)}
          className="app-card-elevated p-0.5 inline-flex"
        >
          <ToggleGroupItem
            value="distance"
            disabled={!bounds.hasDistance}
            className="rounded-md text-[10px] tracking-[0.2em] uppercase px-4 py-1.5 text-dark-text-secondary hover:text-dark-text-primary data-[state=on]:bg-brand-primary data-[state=on]:text-black data-[state=on]:shadow-sm disabled:opacity-40"
            data-testid="range-mode-distance"
          >
            Distance
          </ToggleGroupItem>
          <ToggleGroupItem
            value="time"
            className="rounded-md text-[10px] tracking-[0.2em] uppercase px-4 py-1.5 text-dark-text-secondary hover:text-dark-text-primary data-[state=on]:bg-brand-primary data-[state=on]:text-black data-[state=on]:shadow-sm"
            data-testid="range-mode-time"
          >
            Time
          </ToggleGroupItem>
        </ToggleGroup>
      </div>

      <SliderPrimitive.Root
        className="relative flex w-full touch-none select-none items-center py-3"
        min={0}
        max={domainMax}
        step={step}
        value={domainValues}
        onValueChange={handleSliderChange}
        onValueCommit={handleSliderCommit}
        minStepsBetweenThumbs={1}
        data-testid="range-slider"
      >
        <SliderPrimitive.Track
          ref={panRef}
          className="relative h-2.5 w-full grow overflow-hidden rounded-md bg-[var(--surface-secondary)] border border-[var(--border-primary)]"
        >
          <SliderPrimitive.Range
            className="absolute h-full bg-brand-primary/70 cursor-grab active:cursor-grabbing rounded-md"
            onPointerDown={(e) => {
              if (e.button !== 0) return;
              panStateRef.current = {
                startX: e.clientX,
                startVal: domainValues[0],
                endVal: domainValues[1],
                span: domainValues[1] - domainValues[0],
              };
              e.preventDefault();
            }}
          />
        </SliderPrimitive.Track>
        <SliderPrimitive.Thumb
          className="block h-4 w-3 rounded-sm border-2 border-brand-primary bg-[var(--surface-primary)] shadow focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-primary"
          aria-label="Range start"
        />
        <SliderPrimitive.Thumb
          className="block h-4 w-3 rounded-sm border-2 border-brand-primary bg-[var(--surface-primary)] shadow focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-primary"
          aria-label="Range end"
        />
      </SliderPrimitive.Root>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 items-end">
        <label className="block">
          <span className="card-label">
            {mode === "distance" && bounds.hasDistance ? `Start (${unit})` : "Start time"}
          </span>
          <input
            type="text"
            value={startInput}
            onChange={(e) => setStartInput(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && applyManualInputs()}
            className="app-control mt-1 text-xs font-mono"
            data-testid="range-input-start"
          />
        </label>
        <label className="block">
          <span className="card-label">
            {mode === "distance" && bounds.hasDistance ? `End (${unit})` : "End time"}
          </span>
          <input
            type="text"
            value={endInput}
            onChange={(e) => setEndInput(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && applyManualInputs()}
            className="app-control mt-1 text-xs font-mono"
            data-testid="range-input-end"
          />
        </label>
        <button
          type="button"
          onClick={applyManualInputs}
          className="btn-primary-app px-3 py-2 text-[10px] tracking-[0.2em] uppercase"
          data-testid="range-apply-inputs"
        >
          Apply Range
        </button>
        <button
          type="button"
          onClick={() => onRangeChange(0, domainMax, { commit: true, reset: true })}
          className="btn-secondary-app px-3 py-2 text-[10px] tracking-[0.2em] uppercase"
          data-testid="range-reset"
        >
          Reset Full Activity
        </button>
      </div>
    </div>
  );
};
